
const LoanProcess = () => {
  const processes = [
    {
      step: '1',
      title: 'Application Submission',
      description: 'Complete your loan application with required documentation',
      icon: 'ri-file-text-line'
    },
    {
      step: '2',
      title: 'Document Review',
      description: 'Our team reviews your application and supporting documents',
      icon: 'ri-search-line'
    },
    {
      step: '3',
      title: 'Credit Assessment',
      description: 'Comprehensive evaluation of your financial situation',
      icon: 'ri-bar-chart-line'
    },
    {
      step: '4',
      title: 'Approval & Funding',
      description: 'Upon approval, funds are deposited to your Cooperative savings account',
      icon: 'ri-check-line'
    }
  ];

  const loanProcessDetails = [
    {
      type: 'Personal Loans',
      icon: 'ri-hand-heart-line',
      process: [
        'Complete loan application form with personal identification and proof of income',
        'Submit recent bank statements and employment verification',
        'Undergo credit assessment by Cooperative loan officer',
        'Upon approval, sign loan agreement and receive funds in your Landmark savings account'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    },
    {
      type: 'Business Start-Up Loans',
      icon: 'ri-building-line',
      process: [
        'Provide detailed business plan with loan application',
        'Present financial projections and business registration documentation',
        'Attend interview or pitch session with Cooperative advisor',
        'Once approved, agree to terms and receive funding for your venture'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    },
    {
      type: 'Education Loans',
      icon: 'ri-graduation-cap-line',
      process: [
        'Apply with proof of enrollment at recognized educational institution',
        'Submit tuition invoices and identification documents',
        'Provide guarantor information if required',
        'Upon approval, funds paid directly to institution or your account'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    },
    {
      type: 'Settlement Loans',
      icon: 'ri-home-heart-line',
      process: [
        'Show proof of residency status and settlement expense documentation',
        'Fill out loan application with contact details and references',
        'Attend brief orientation on financial literacy and repayment expectations',
        'Once approved, funds issued to cover specific settlement costs'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    },
    {
      type: 'Vehicle Loans',
      icon: 'ri-car-line',
      process: [
        'Submit loan application with proof of income and vehicle quotation',
        'Provide valid driver\'s license and insurance details',
        'Undergo credit assessment',
        'Upon approval, funds transferred to dealership or seller'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    },
    {
      type: 'Home Improvement Loans',
      icon: 'ri-hammer-line',
      process: [
        'Provide renovation plans and contractor estimates with loan application',
        'Submit proof of property ownership and income statements',
        'After approval, funds released directly to contractors or member account'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    },
    {
      type: 'Microloans',
      icon: 'ri-coins-line',
      process: [
        'Complete simplified loan application describing project and funding needed',
        'Submit basic identification and project cost breakdown',
        'Receive expedited approval',
        'Direct deposit of funds to your Cooperative savings account'
      ],
      requirements: [
        'Proof of employment and ability to pay',
        'Reasonable collateral',
        'Participation in financial literacy workshops',
        'Agreement to flexible repayment terms'
      ]
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Loan Process
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple, transparent, and member-focused approach to lending
          </p>
        </div>

        {/* General Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          {processes.map((process, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className={`${process.icon} text-xl`}></i>
              </div>
              <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                {process.step}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h3>
              <p className="text-gray-600 text-sm">{process.description}</p>
            </div>
          ))}
        </div>

        {/* Maximum Borrowing Amounts */}
        <div className="bg-blue-50 rounded-xl p-8 mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Maximum Borrowing Amounts
            </h3>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Lending limits depend on loan type, project scope, collateral offered, and member's ability to repay. 
              Our finance team will provide specific amounts based on your individual circumstances.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6 text-center">
            <i className="ri-calculator-line text-4xl text-blue-600 mb-4"></i>
            <h4 className="text-xl font-semibold text-gray-900 mb-2">
              Personalized Lending Limits
            </h4>
            <p className="text-gray-600 mb-4">
              Contact our finance team for detailed information about maximum borrowing amounts in Canadian Dollars
            </p>
            <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
              Contact Finance Team
            </button>
          </div>
        </div>

        {/* Detailed Process by Loan Type */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            Detailed Process by Loan Type
          </h3>
          <div className="space-y-8">
            {loanProcessDetails.map((loan, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mr-4">
                    <i className={`${loan.icon} text-xl`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">{loan.type}</h4>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h5 className="text-lg font-semibold text-gray-900 mb-4">Application Process</h5>
                    <ul className="space-y-3">
                      {loan.process.map((step, stepIndex) => (
                        <li key={stepIndex} className="flex items-start">
                          <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                            {stepIndex + 1}
                          </span>
                          <span className="text-gray-700 text-sm">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h5 className="text-lg font-semibold text-gray-900 mb-4">General Requirements</h5>
                    <ul className="space-y-2">
                      {loan.requirements.map((requirement, reqIndex) => (
                        <li key={reqIndex} className="flex items-center text-sm text-gray-700">
                          <i className="ri-check-line text-teal-600 mr-2"></i>
                          {requirement}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Membership Benefits */}
        <div className="bg-teal-50 rounded-xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Membership Benefits
            </h3>
            <p className="text-gray-600 max-w-3xl mx-auto">
              All loan funds are deposited directly into your Landmark Cooperative savings account, 
              providing secure and accessible financial management.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <i className="ri-bank-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-semibold text-gray-900 mb-2">Cooperative Savings Account</h4>
              <p className="text-sm text-gray-600">Linked and operated by a bank for security and convenience</p>
            </div>
            <div className="text-center">
              <i className="ri-money-dollar-circle-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-semibold text-gray-900 mb-2">No Membership Fee</h4>
              <p className="text-sm text-gray-600">Free membership during our startup phase</p>
            </div>
            <div className="text-center">
              <i className="ri-graduation-cap-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-semibold text-gray-900 mb-2">Financial Literacy</h4>
              <p className="text-sm text-gray-600">Mandatory workshops to support your financial success</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoanProcess;
